import { ReactNode, useState, useEffect, useRef, lazy, Suspense } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import MapCanvas from "./scenes/MapCanvas";
import { SCENE_REBUILD_EVENT } from "./scenes/sceneHealth";
import { useMapSelfHealing } from "./scenes/map/useMapSelfHealing";
import RoomCanvas from "./scenes/RoomCanvas";
import useInterfaceStore from "./stores/useInterfaceStore";
import useMapViewStore from "./stores/useMapViewStore";
import useSplashStore from "./stores/useSplashStore";
import CityHallScreen from "./screens/CityHallScreen";
import { ServerConnectGate } from "./ServerConnectGate";
import OpenInTelegramScreen from "./components/OpenInTelegramScreen";
import { isOutsideTelegram } from "./utils/telegram/isOutsideTelegram";
import * as Sentry from "@sentry/react";
import "./App.css";
// Onboarding state (tap/map/welcome/language/sound) лежить у БД
// (таблиця user_onboarding_kv) і читається через useOnboardingState.
// Старі localStorage-прапорці онбордингу повністю прибрані, щоб «Скинути онбординг»
// (DELETE /user/onboarding-state) дійсно повертав користувача на початок флоу.
import { useOnboardingState } from "./hooks/useOnboardingState";
import { getUserSelectedLanguage } from "./utils/userLanguage";
import i18n from "./i18n";
import useSoundStore from "./stores/useSoundStore";
import { useQuestSocket } from "./hooks/useQuestSocket";
import { getUserDataOnUnload, type UserData } from "./models/user";
import { requestWriteAccessWeeklyOnGameSession } from "./utils/telegram/telegramWriteAccess";
import { useQueryClient } from "@tanstack/react-query";
import { useDeepLinkStartParam } from "./hooks/useDeepLinkStartParam";
// import { useUserData } from "./hooks/useUserData"; // Вимкнено разом з ERUDA

/** Onboarding always begins with the onboarding stories. */
const ONBOARDING_START_PATH = "/onboarding-stories";

/** Раніше не було path="/" — при відкритті з кореня URL Routes не мали збігу → темний екран. */
function DefaultRouteRedirect() {
  const { hasOnboardingCompleted } = useOnboardingState();

  if (hasOnboardingCompleted()) return <Navigate to="/map" replace />;
  return <Navigate to={ONBOARDING_START_PATH} replace />;
}

// Lazy load компонент для статичних i18n-рядків (завантажується тільки при прямому відкритті URL)
const I18nStaticStringsScreen = lazy(() => import("./screens/I18nStaticStringsScreen"));

// Every screen but the map (the default route) is its own chunk: the first paint
// downloads and parses only what the map needs. They are fetched in the
// background once the map is up (prefetchScreens), so opening one stays instant.
const screenChunks = {
  StartEldersDialog: () => import("./screens/StartEldersDialog"),
  OnboardingStories: () => import("./scenes/onboardingStories/OnboardingStories"),
  TapScreen: () => import("./screens/TapScreen"),
  Top100Screen: () => import("./screens/Top100Screen"),
  ShopScreen: () => import("./screens/ShopScreen"),
  QuestsScreen: () => import("./screens/QuestsScreen"),
  HousesScreen: () => import("./screens/HousesScreen"),
  LanguageSelectionScreen: () => import("./screens/LanguageSelectionScreen"),
  SoundSelectionScreen: () => import("./screens/SoundSelectionScreen"),
  SettingsScreen: () => import("./screens/SettingsScreen"),
  WithdrawScreen: () => import("./screens/WithdrawScreen"),
};
const StartEldersDialog = lazy(screenChunks.StartEldersDialog);
const OnboardingStories = lazy(screenChunks.OnboardingStories);
const TapScreen = lazy(screenChunks.TapScreen);
const Top100Screen = lazy(screenChunks.Top100Screen);
const ShopScreen = lazy(screenChunks.ShopScreen);
const QuestsScreen = lazy(screenChunks.QuestsScreen);
const HousesScreen = lazy(screenChunks.HousesScreen);
const LanguageSelectionScreen = lazy(screenChunks.LanguageSelectionScreen);
const SoundSelectionScreen = lazy(screenChunks.SoundSelectionScreen);
const SettingsScreen = lazy(screenChunks.SettingsScreen);
const WithdrawScreen = lazy(screenChunks.WithdrawScreen);

let screensPrefetched = false;
function prefetchScreens(): void {
  if (screensPrefetched) return;
  screensPrefetched = true;
  for (const load of Object.values(screenChunks)) void load().catch(() => { /* loaded on demand */ });
}


// Self-check for the Traceway pipeline. Only routed when telemetry is actually
// configured, so it cannot be opened in a build that has nowhere to report to.
const TracewayTestScreen = lazy(() => import("./screens/TracewayTestScreen"));
const tracewayConfigured = Boolean(import.meta.env.VITE_TRACEWAY_CONNECTION_STRING);

// Never ship local/e2e traffic to Sentry (also keeps E2E whitelist clean).
if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://5244e7d374a321a79776c6d372b1a50f@o4509340100395008.ingest.de.sentry.io/4509340160688208",
    sendDefaultPii: true,
    // No session replay: it records every DOM change all session long (rrweb) to
    // replay a canvas game it cannot see — load and lag on phones for nothing.
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      "https://notapi.web3islands.com",
      "localhost",
      /^https:\/\/.*\.ngrok-free\.app/,
    ],
  });
}

// конфіг початкового роута
export const startWithStories = true;
export const nouvelleShowAtStart = true;

function RequireBackendConnection({ children }: { children: ReactNode }) {
  const [backendAvailable, setBackendAvailable] = useState(false);
  const [splashDone, setSplashDone] = useState(false);
  // The splash's running connect keeps the callbacks of its first render: a ref,
  // not state, tells them the game is already set up.
  const connectedRef = useRef(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    hasOnboardingCompleted,
    hasLanguageSelectionBeenShown,
    hasSoundSelectionBeenShown,
  } = useOnboardingState();

  // Opened in a plain browser: the game only works inside Telegram.
  if (isOutsideTelegram()) return <OpenInTelegramScreen />;

  // The player's data is in: set up and mount the game — under the splash.
  const connect = () => {
    if (connectedRef.current) return;
    connectedRef.current = true;
    const lang = getUserSelectedLanguage();
    if (lang) {
      void i18n.changeLanguage(lang);
    }
    try {
      const onboarding = queryClient.getQueryData<{ sounds_enabled: boolean | null }>(["onboarding-state"]);
      if (onboarding?.sounds_enabled !== null && onboarding?.sounds_enabled !== undefined) {
        useSoundStore.setState({ soundsEnabled: !!onboarding.sounds_enabled });
      }
    } catch {
      /* ignore */
    }
    // Onboarding is the single entry point: an unfinished one always starts
    // from the beginning, whatever URL / deep link the app was opened with.
    // (The server has already wiped its progress for this new app session.)
    const onboardingState = queryClient.getQueryData<{ onboarding_completed?: boolean }>(["onboarding-state"]);
    if (onboardingState && !onboardingState.onboarding_completed) {
      navigate(ONBOARDING_START_PATH, { replace: true });
    }
    setBackendAvailable(true);
  };

  // One splash from the first frame until it closes (always the last child, so
  // it is never remounted): the game is built underneath it and appears whole.
  const splash = splashDone ? null : (
    <div className="fixed inset-0 z-[100000]">
      <ServerConnectGate
        onConnected={connect}
        onSuccess={() => {
          connect();
          setSplashDone(true);
          useSplashStore.setState({ visible: false });
        }}
      />
    </div>
  );

  return (
    <>
      {backendAvailable ? gameContent() : null}
      {splash}
    </>
  );

  // 2. Onboarding data is loaded blocking in ServerConnectGate, now check conditions
  function gameContent(): ReactNode {
    if (hasOnboardingCompleted()) {
      return children;
    }

    if (!hasLanguageSelectionBeenShown()) {
      return (
        <Suspense fallback={null}>
        <LanguageSelectionScreen 
          onConfirm={() => {
            // Invalidate cache to refresh onboarding state, no UI blocking
            queryClient.invalidateQueries({ queryKey: ['onboarding-state'] }).catch(() => {});
          }} 
        />
        </Suspense>
      );
    }

    if (!hasSoundSelectionBeenShown()) {
      return (
        <Suspense fallback={null}>
        <SoundSelectionScreen
          onConfirm={() => {
            // Invalidate cache to refresh onboarding state, no UI blocking  
            queryClient.invalidateQueries({ queryKey: ['onboarding-state'] }).catch(() => {});
          }}
        />
        </Suspense>
      );
    }

    // 3. Все вибрано — показуємо children (stories → dialog → tap)
    return children;
  }
}

function MapOrRoomView() {
  const { currentView, currentRoomMapObjectId } = useInterfaceStore();

  // Self-healing: a broken scene (dead textures after a lost WebGL context) asks to
  // be rebuilt — a new key remounts it through the normal mount path (sceneHealth.ts).
  const [sceneEpoch, setSceneEpoch] = useState(0);
  useEffect(() => {
    const rebuild = () => setSceneEpoch((e) => e + 1);
    window.addEventListener(SCENE_REBUILD_EVENT, rebuild);
    return () => window.removeEventListener(SCENE_REBUILD_EVENT, rebuild);
  }, []);
  useMapSelfHealing(currentView !== 'room');

  // DEV-ONLY screenshot harness: `?room=<id>` enters that room interior directly
  // (bypasses map navigation) so headless-browser regression screenshots are deterministic.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    // `?room=<id>` is the hand-driven twin of the global the harness sets: the
    // headless probes can write a global before the bundle runs, a human opening
    // a URL cannot.
    const rid = window.__E2E_ROOM ?? new URLSearchParams(window.location.search).get('room');
    if (!rid) return;
    // Let MapCanvas mount + initialize the shared PIXI singleton first, THEN enter the room.
    const t = setTimeout(() => {
      useInterfaceStore.getState().setRoomView(Number(rid), null);
      console.log('[HARNESS] setRoomView done, view=', useInterfaceStore.getState().currentView);
    }, 3500);
    return () => clearTimeout(t);
  }, []);

  if (currentView === 'room' && currentRoomMapObjectId !== null) {
    // Keyed by room too: the tutorial's practice room hands over to the real house in place.
    return <RoomCanvas key={`${sceneEpoch}:${currentRoomMapObjectId}`} mapObjectId={currentRoomMapObjectId} />;
  }

  return <MapCanvas key={sceneEpoch} />;
}

function StartEldersDialogRoute() {
  const { hasOnboardingCompleted } = useOnboardingState();

  if (hasOnboardingCompleted()) {
    return <Navigate to="/map" replace />;
  }

  return <StartEldersDialog />;
}

function OnboardingStoriesRoute() {
  const navigate = useNavigate();
  const { hasOnboardingCompleted } = useOnboardingState();

  if (hasOnboardingCompleted()) {
    return <Navigate to="/map" replace />;
  }

  return (
    <OnboardingStories
      onFinish={() => {
        navigate("/start-dialog", { replace: true });
      }}
    />
  );
}

function App() {
  const location = useLocation();
  const { data: onboardingData } = useOnboardingState();
  const queryClient = useQueryClient();


  // The other screens' code, once the map is drawn (or a little later anyway).
  const markersReady = useMapViewStore((s) => s.markersReady);
  useEffect(() => {
    if (markersReady) { prefetchScreens(); return; }
    const id = window.setTimeout(prefetchScreens, 8000);
    return () => window.clearTimeout(id);
  }, [markersReady]);

  // Real-time інвалідація квестів через Socket.IO
  useQuestSocket();

  // Parse Telegram startapp deep-link params (plot_<id>, cityhall) once on mount.
  useDeepLinkStartParam();

  // Ініціалізація ERUDA для адміністраторів (ВИМКНЕНО)
  // ERUDA ламала Instagram stories: прогресбар доходив до ~20% і потім починав заново,
  // історії зависали і не переходили автоматично до наступної, потрібно було вручну натискати.
  // Проблема: ERUDA додає елементи в DOM і перехоплює події, що впливає на роботу react-insta-stories,
  // зокрема на таймери та обробку кліків для анімації прогресбару.
  // useEffect(() => {
  //   const { data: userData } = useUserData();
  //   if (userData?.is_admin && typeof window !== 'undefined') {
  //     // Перевіряємо, чи ERUDA вже завантажена
  //     if (!window.eruda) {
  //       const script = document.createElement('script');
  //       script.src = 'https://cdn.jsdelivr.net/npm/eruda';
  //       script.onload = () => {
  //         window.eruda.init();
  //         console.log('[App] ERUDA initialized for admin');
  //       };
  //       document.head.appendChild(script);
  //     } else {
  //       window.eruda.init();
  //       console.log('[App] ERUDA already loaded, re-initialized');
  //     }
  //   }
  // }, [userData?.is_admin]);

  // Ініціалізація Telegram WebApp після завантаження додатку
  useEffect(() => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.ready();
      if (!window.Telegram.WebApp.isExpanded) {
        window.Telegram.WebApp.expand();
      }
    }
  }, []);

  // Request write access for returning players — at most once per 7 days,
  // only after tap or map onboarding has been recorded on the server (not raw new installs).
  useEffect(() => {
    const verified = !!queryClient.getQueryData<UserData>(['userData'])?.notifications_warmup_granted;
    requestWriteAccessWeeklyOnGameSession(queryClient, verified);
  }, [
    queryClient,
    onboardingData?.onboarding_completed,
    onboardingData?.write_access_last_prompted_at_ms,
  ]);

  // NOTE: forced "welcome to main map" modal removed — it was redundant and could
  // pop over other screens (TapScreen) after navigation.

  // React Hook для фіксації в'юпорту тільки під час введення тексту
  useEffect(() => {
    const doc = document.documentElement;
    
    // Функція для встановлення фіксованої висоти
    const setFixedHeight = () => {
      const height = window.innerHeight;
      doc.style.setProperty('--app-height', `${height}px`);
    };
    
    // Функція для прибирання фіксованої висоти
    const removeFixedHeight = () => {
      doc.style.removeProperty('--app-height');
    };
    
    // Обробник фокусу на текстових полях
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        setFixedHeight();
      }
    };
    
    // Обробник втрати фокусу з текстових полів
    const handleBlur = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        // Невелика затримка, щоб переконатися, що клавіатура вже закрилася.
        // ВАЖЛИВО: при переході фокусу input→input (клавіатура лишається відкритою)
        // не знімаємо фіксацію, якщо новий активний елемент — теж текстове поле.
        // Інакше відбувається різкий стрибок/звуження в момент перемикання полів.
        setTimeout(() => {
          const active = document.activeElement as HTMLElement | null;
          const stillEditing =
            !!active &&
            (active.tagName === 'INPUT' ||
              active.tagName === 'TEXTAREA' ||
              active.tagName === 'SELECT' ||
              active.isContentEditable);
          if (!stillEditing) {
            removeFixedHeight();
          }
        }, 100);
      }
    };
    
    // Додаємо глобальні обробники через делегування подій
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);
    
    // Прибираємо обробники при розмонтуванні компонента
    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
    };
  }, []);

  // Обробка закриття додатку - викликаємо calculate-user для обрахунку стану користувача
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Викликаємо calculate-user при закритті вкладки/додатку
      getUserDataOnUnload();
    };

    const handleVisibilityChange = () => {
      // Коли користувач переключається на іншу вкладку або закриває додаток
      if (document.visibilityState === "hidden") {
        getUserDataOnUnload();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Спеціальний роут для статичних i18n-рядків (використовується лише для потрапляння ключів у локалі)
  // Завантажується тільки при прямому відкритті URL (lazy loading)
  if (location.pathname === "/i18n-static-translations") {
    return (
      <Suspense fallback={null}>
        <I18nStaticStringsScreen />
      </Suspense>
    );
  }

  // Deliberately ahead of RequireBackendConnection: whether telemetry reaches
  // Traceway is a separate question from whether the game backend is up, and
  // the screen is useless for diagnosis if a backend outage hides it.
  if (tracewayConfigured && location.pathname === "/traceway-test") {
    return (
      <Suspense fallback={null}>
        <TracewayTestScreen />
      </Suspense>
    );
  }

  // Для всіх екранів (які використовують ActiveGameZoneScreenWithNavigation) - з навбаром
  // /onboarding-stories використовує ActiveGameZoneScreen (без навігації), тому навбар не показується
  return (
    <RequireBackendConnection>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100vw",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Контент екранів - може рости, має власний вертикальний скрол */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            position: "relative",
          }}
        >
          <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<DefaultRouteRedirect />} />
            <Route path="/map" element={<MapOrRoomView />} />
            <Route path="/tap" element={<TapScreen />} />
            <Route path="/top" element={<Top100Screen />} />
            <Route path="/shop" element={<ShopScreen />} />
            <Route path="/quests" element={<QuestsScreen />} />
            <Route path="/my-houses" element={<HousesScreen />} />
            <Route path="/menu" element={<SettingsScreen />} />
            <Route path="/withdraw" element={<WithdrawScreen />} />
            <Route
              path="/start-dialog"
              element={<StartEldersDialogRoute />}
            />
            <Route path="/cityhall" element={<CityHallScreen />} />
            <Route
              path={ONBOARDING_START_PATH}
              element={<OnboardingStoriesRoute />}
            />
            <Route path="*" element={<DefaultRouteRedirect />} />
          </Routes>
          </Suspense>
        </div>
      </div>
    </RequireBackendConnection>
  );
}

export default App;
